<template>
  <div class="container flow-test-instance" :class="{ 'flow-test-instance--fullscreen': fullscreen }">
    <template v-if="loading">
      <div class="flow-test-skeleton-boot">
        <BaseSkeletonLoading variant="form" />
        <BaseSkeletonLoading variant="panel" style="margin-top:12px;max-width:360px;" />
      </div>
    </template>
    <template v-else>
      <div class="flow-test-titlebar">
        <BaseIcon class="flow-test-title-icon" :icon="flow.icon || 'lianjieliu'" :size="18" />
        <span class="flow-test-title">{{ displayTitle }}</span>
        <span class="flow-test-sub">{{ phaseSub }}</span>
      </div>
      <div class="flow-test-body flex-wrap flex-between align-start">
        <div class="flow-test-main">
          <div class="flow-test-content">
            <!-- 运行中且当前身份无权查看 -->
            <template v-if="isRunning && !access.canView">
              <BaseEmpty msg="当前身份暂无查看权限" />
            </template>
            <!-- 配置阶段：可编辑发起表单 -->
            <template v-else-if="isConfig">
              <BaseSkeletonLoading v-if="formLoading" variant="form" />
              <UgoForm
                v-else-if="form && (form.id || (form.fields && form.fields.length))"
                ref="form"
                :value="formData"
                :form="form"
                :acl="acl"
                :editable="true"
                :as_view="false"
                style="background:none;"
                @input="handleFormInput"
              />
              <div v-else class="flow-test-soft-tip">开始节点未绑定视图，请先在开始节点选择表单</div>
            </template>
            <!-- 运行阶段：左侧仅表单视图 -->
            <template v-else-if="isRunning">
              <BaseSkeletonLoading v-if="formLoading" variant="form" />
              <UgoForm
                v-else-if="access.canView && form && (form.id || (form.fields && form.fields.length))"
                ref="form"
                :key="'view-' + currentViewNodeId + '-' + (form && form.id) + '-' + ((cloneFormContext && cloneFormContext.keyPart) || '') + '-' + aclRenderKey"
                :value="formData"
                :form="form"
                :acl="acl"
                :clone-context="cloneFormContext"
                :editable="formEditable"
                :as_view="!formEditable"
                style="background:none;"
                @input="handleFormInput"
              />
              <div v-else-if="access.canView" class="flow-test-soft-tip">当前节点未绑定视图</div>
            </template>
            <template v-else>
              <BaseEmpty msg="暂无内容" />
            </template>
          </div>

          <!-- 配置/测试阶段：发起按钮；正式实例页不占底部操作行 -->
          <div v-if="isConfig" class="flex-wrap flex-right action-row">
            <Button
              long
              type="primary"
              size="large"
              style="width:180px;"
              :loading="acting"
              @click="startTest"
            >发起测试</Button>
          </div>
          <div v-if="errorMsg && isConfig" class="error-msg">{{ errorMsg }}</div>
        </div>

        <div class="side-panel">
          <div class="side-graph-wrap">
            <FlowGraph
              v-if="graphData && graphData.nodes"
              :key="'flow-graph-' + graphRev"
              class="side-graph"
              :data="graphData"
              :status-map="graphStatusMap"
            />
            <div v-else class="side-graph-empty">流程图加载中…</div>
          </div>

          <!-- 配置阶段：右侧配置节点与人员（含 clone 分类） -->
          <template v-if="isConfig">
            <div class="side-section-title">流程配置</div>
            <div class="node-config-panel">
              <Steps :current="0" direction="vertical">
                <template v-for="(l, li) in nodeLevels">
                  <Step :key="'lv-' + li">
                    <div slot="content">
                      <template v-for="n in l">
                        <NodeCreate
                          v-if="needsNodeConfig(n)"
                          :key="n.id"
                          :value="nodeData[n.id]"
                          :node="n"
                          :db="formData"
                          :readonly="isCloneFollower(n)"
                          @input="onNodeConfigInput(n.id, $event)"
                        />
                      </template>
                    </div>
                  </Step>
                </template>
              </Steps>
              <BaseSkeletonLoading v-if="!nodeLevels.length || formLoading" variant="panel" />
            </div>
          </template>

          <!-- 运行阶段：待办（含操作）+ 历史 -->
          <template v-else>
            <div class="todo-card" :class="{ 'todo-card--timeout': primaryTodo && primaryTodo.timeoutWarning }">
              <template v-if="access.canAct || displayTodoCards.length || withdrawAction">
                <div
                  v-if="displayTodoCards.length > 1"
                  class="todo-thread-switch"
                >
                  <button
                    v-for="t in displayTodoCards"
                    :key="t.id"
                    type="button"
                    class="todo-thread-chip"
                    :class="{
                      'todo-thread-chip--active': primaryTodo && primaryTodo.id === t.id,
                      'todo-thread-chip--peer': t.peerView,
                    }"
                    @click="selectTodoTask(t.id)"
                  >
                    {{ t.cloneKey || t.name || '待办' }}
                    <span v-if="t.peerView" class="todo-thread-chip-tag">只读</span>
                  </button>
                </div>
                <div v-if="primaryTodo" class="todo-head">
                  <div class="todo-title" :title="primaryTodo.name">{{ primaryTodo.name }}</div>
                  <div class="todo-attrs">
                    <span class="todo-attr" :title="typeLabel(primaryTodo.type)">
                      <BaseIcon :icon="typeIcon(primaryTodo.type)" :size="14" />
                    </span>
                    <span v-if="primaryTodo.parallel" class="todo-attr" title="并行线程">
                      <BaseIcon icon="md-git-branch" :size="14" />
                    </span>
                    <span v-if="primaryTodo.peerView" class="todo-attr todo-attr--peer" title="平行节点（只读）">
                      只读
                    </span>
                    <span v-if="primaryTodo.weakSkip" class="todo-attr todo-attr--warn" title="可跳过上一弱审批">
                      <BaseIcon icon="md-flash" :size="14" />
                    </span>
                    <span v-else-if="primaryTodo.weak" class="todo-attr todo-attr--warn" title="弱审批">
                      <BaseIcon icon="md-flash" :size="14" />
                    </span>
                    <span v-if="primaryTodo.cloneKey" class="todo-attr" :title="'分类：' + primaryTodo.cloneKey">
                      <BaseIcon icon="md-pricetag" :size="14" />
                      <em>{{ primaryTodo.cloneKey }}</em>
                    </span>
                    <span
                      v-if="primaryTodo.deadlineAt"
                      class="todo-attr"
                      :class="{ 'todo-attr--warn': primaryTodo.timeoutWarning }"
                      :title="primaryTodo.timeoutWarning ? '已超时' : ('时效至 ' + formatTime(primaryTodo.deadlineAt))"
                    >
                      <BaseIcon icon="md-time" :size="14" />
                    </span>
                    <span class="todo-attr todo-attr--people" :title="'执行人：' + assigneeNames(primaryTodo)">
                      <BaseIcon icon="md-people" :size="14" />
                      <em>{{ assigneeNames(primaryTodo) }}</em>
                    </span>
                  </div>
                </div>
                <BaseEmpty v-else-if="!displayTodoCards.length && !withdrawAction" msg="暂无待办" />

                <template v-if="canFillPrimaryTodo">
                  <!-- 必须/可选：意见 + 附件 +（非弱审批时）同意/退回 + 提交 -->
                  <template v-if="showOpinionPanel">
                    <div class="todo-opinion-label">意见{{ opinionRequired ? '' : '（可选）' }}</div>
                    <Input
                      v-model="comment"
                      type="textarea"
                      :rows="2"
                      :placeholder="opinionRequired ? '请填写审批意见' : '审批意见（可选）'"
                      class="todo-comment"
                    />
                    <div v-if="!isPrimaryTodoWeak" class="todo-agree-row">
                      <span class="todo-opinion-label">是否同意</span>
                      <RadioGroup v-model="opinionAgree">
                        <Radio label="agree">同意</Radio>
                        <Radio label="reject">退回</Radio>
                      </RadioGroup>
                    </div>
                    <div class="todo-attach">
                      <BaseFileSelectFull
                        :value="actionAttachments"
                        :option="{ max: 20 }"
                        @input="actionAttachments = $event || []"
                      />
                    </div>
                    <div class="todo-actions">
                      <Button
                        type="primary"
                        long
                        class="todo-action-btn todo-action-btn--primary"
                        :loading="acting"
                        @click="submitWithOpinion"
                      >提交</Button>
                    </div>
                  </template>
                  <!-- 表单：非弱审批时同意/退回 + 提交；弱审批仅提交（意见在表单内） -->
                  <template v-else-if="showFormOpinionSubmit">
                    <div v-if="!isPrimaryTodoWeak" class="todo-agree-row">
                      <span class="todo-opinion-label">是否同意</span>
                      <RadioGroup v-model="opinionAgree">
                        <Radio label="agree">同意</Radio>
                        <Radio label="reject">退回</Radio>
                      </RadioGroup>
                    </div>
                    <div class="todo-actions">
                      <Button
                        type="primary"
                        long
                        class="todo-action-btn todo-action-btn--primary"
                        :loading="acting"
                        @click="submitWithOpinion"
                      >提交</Button>
                    </div>
                  </template>
                  <!-- 非审批意见模式（开始/抄送等）：原动作按钮 -->
                  <template v-else>
                    <div class="todo-actions">
                      <template v-for="act in primaryActions">
                        <Button
                          :key="act.key + (act.nodeId || '') + (act.taskId || '')"
                          type="primary"
                          long
                          class="todo-action-btn todo-action-btn--primary"
                          :loading="acting"
                          @click="runAction(act)"
                        >{{ act.label }}</Button>
                      </template>
                      <template v-for="act in secondaryActions">
                        <Button
                          :key="act.key + (act.nodeId || '') + (act.taskId || '') + '-s'"
                          long
                          class="todo-action-btn"
                          :loading="acting"
                          @click="runAction(act)"
                        >{{ act.label }}</Button>
                      </template>
                    </div>
                  </template>
                </template>
                <template v-else-if="primaryTodo && primaryTodo.peerView">
                  <BaseEmpty msg="平行节点仅可查看，请切换到本人线程填写" />
                </template>
                <!-- 无待办但仍可撤回（含克隆并行刚提交、下一节点未处理） -->
                <div
                  v-if="withdrawAction && !canFillPrimaryTodo"
                  class="todo-withdraw-only"
                >
                  <div v-if="withdrawHint" class="todo-withdraw-hint">{{ withdrawHint }}</div>
                </div>
                <div v-if="withdrawAction" class="todo-withdraw-row">
                  <Button
                    long
                    class="todo-action-btn todo-action-btn--withdraw"
                    :loading="acting"
                    @click="runAction(withdrawAction)"
                  >撤回</Button>
                </div>
              </template>
              <template v-else>
                <BaseEmpty :msg="access.canView ? '当前身份无可处理事项（只读）' : '当前身份暂无审核权限'" />
              </template>
              <div v-if="errorMsg && isRunning" class="error-msg">{{ errorMsg }}</div>
            </div>

            <div class="history-block">
              <div class="history-block-head">
                <div class="history-tabs">
                  <button
                    type="button"
                    class="history-tab"
                    :class="{ 'history-tab--active': sideInfoTab === 'history' }"
                    @click="sideInfoTab = 'history'"
                  >历史记录</button>
                  <button
                    type="button"
                    class="history-tab"
                    :class="{ 'history-tab--active': sideInfoTab === 'files' }"
                    @click="sideInfoTab = 'files'"
                  >文件<span v-if="processFiles.length" class="history-tab-count">{{ processFiles.length }}</span></button>
                </div>
                <Button
                  v-if="sideInfoTab === 'history'"
                  type="text"
                  size="small"
                  class="history-order-toggle"
                  @click="toggleHistoryOrder"
                >{{ historyNewestFirst ? '正序' : '倒序' }}</Button>
              </div>
              <div v-if="sideInfoTab === 'history'" class="history-timeline">
                <div
                  v-for="h in displayHistoryNodes"
                  :key="h.id + '-' + (h.created_at || '')"
                  class="history-row"
                  :class="{
                    'history-row--skip': h.action === 'weak_skip' || h.action === 'timeout_skip' || (h.meta && h.meta.skipped),
                    'history-row--warn': h.action === 'timeout_warn',
                  }"
                >
                  <div class="history-rail">
                    <span class="history-dot" />
                    <span class="history-line" />
                  </div>
                  <div class="history-main">
                    <div class="history-person">
                      <BaseNameBoard
                        v-if="h.created_by && h.created_by !== 'system'"
                        :uid="h.created_by"
                        :size="32"
                      />
                      <div v-else class="sys-actor-row">
                        <div class="sys-actor-avatar" title="系统">系</div>
                        <span class="sys-actor-name">系统</span>
                      </div>
                    </div>
                    <div class="history-body">
                      <div class="history-meta">
                        <span class="history-time">{{ formatTime(h.created_at) }}</span>
                        <span v-if="h.action === 'timeout_skip'" class="skip-tag">超时跳过</span>
                        <span v-else-if="h.action === 'weak_skip' || (h.meta && h.meta.skipped)" class="skip-tag">跳过</span>
                        <span v-else-if="h.action === 'timeout_warn'" class="sla-tag sla-tag--warn">超时预警</span>
                      </div>
                      <div v-if="h.nodeName" class="history-node">{{ h.nodeName }}</div>
                      <div class="history-content">{{ h.content }}</div>
                      <div v-if="h.attachments && h.attachments.length" class="history-files">
                        <a
                          v-for="(f, fi) in h.attachments"
                          :key="(f.id || f.url || f.name) + '-' + fi"
                          class="history-file-link"
                          :href="filePreviewUrl(f)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <BaseIcon icon="md-attach" :size="12" style="margin-right:2px;" />{{ f.name || '附件' }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <BaseEmpty v-if="!displayHistoryNodes.length" msg="暂无历史" />
              </div>
              <div v-else class="process-files-panel">
                <div
                  v-for="(f, fi) in processFiles"
                  :key="(f.uid || f.file_id || f.id || f.name) + '-' + fi"
                  class="process-file-row"
                >
                  <a
                    class="process-file-main"
                    :href="filePreviewUrl(f)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      v-if="isProcessImage(f)"
                      class="process-file-thumb"
                      :src="filePreviewUrl(f)"
                      :alt="f.name || '图片'"
                    >
                    <BaseIcon
                      v-else
                      class="process-file-icon"
                      :icon="processFileIcon(f)"
                      :size="18"
                    />
                    <div class="process-file-meta">
                      <div class="process-file-name" :title="f.name">{{ f.name || '未命名文件' }}</div>
                      <div class="process-file-sub">
                        <span v-if="f.type">{{ f.type }}</span>
                        <span v-if="f.source">{{ f.source }}</span>
                      </div>
                    </div>
                  </a>
                </div>
                <BaseEmpty v-if="!processFiles.length" msg="暂无附件或图片" />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 底部工具栏：重置 + 切换身份 -->
      <div v-if="test" class="flow-test-toolbar">
        <Button size="small" class="toolbar-reset" @click="resetTestCache">重置测试</Button>
        <div class="toolbar-status" :class="{ config: isConfig }">{{ statusText }}</div>
        <div class="toolbar-divider" />
        <template v-if="isRunning">
          <div class="actor-bar-label">切换身份</div>
          <div class="actor-list">
            <div
              v-for="a in actors"
              :key="a.id"
              class="actor-chip"
              :class="{
                active: String(actorId) === String(a.id),
                'has-todo': a.canAct || a.pendingCount > 0,
                'no-access': !a.canView && !a.canAct,
              }"
              @click="switchActor(a.id)"
            >
              <BaseAvatar :size="20" :src="a.avatar">{{ (a.name || '?')[0] }}</BaseAvatar>
              <span class="actor-name">{{ a.name }}</span>
              <span v-if="a.roles && a.roles.length" class="actor-role">{{ a.roles[0] }}</span>
              <span v-if="a.canAct || a.pendingCount > 0" class="actor-todo-dot" title="有待办" />
            </div>
            <div v-if="!actors.length" class="actor-empty">发起后未提取到配置人员</div>
          </div>
        </template>
        <div v-else class="toolbar-tip">{{ phaseSub }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import {mapGetters} from 'vuex';
import UgoForm from '../form/index';
import FlowGraph from './graph.vue';
import NodeCreate from '@/components/flow/node/create';
import FlowLocalRuntime from './runtime/FlowLocalRuntime';
import {resolveCloneItemMeta} from '@/components/flow/clone-display';
import {syncCloneExecutorsAcrossNodes, syncAllSharedCloneGroups, isCloneShareFollower} from '@/components/flow/clone-executor-sync';
import {unpackAclMap, prepareFormForAcl, isOpinionEnabled, isOpinionRequired, isOpinionFormMode} from '@/components/flow/flow-acl';
import {buildConfigNodeLevels} from '@/components/flow/flow-version';
import mixinResponsable from '@/mixins/responsable';

export default {
  name: 'FlowInstance',
  components: {UgoForm, FlowGraph, NodeCreate},
  mixins: [mixinResponsable],
  props: {
    'flow-id': {type: String},
    flow: {type: Object, default: () => ({})},
    version: {type: Object, default: () => ({})},
    /** 本地测试模式 */
    test: {type: Boolean, default: false},
    /** 测试时传入当前画布图数据 {nodes,edges} */
    graph: {type: Object, default: null},
    /** 全屏测试对话框内使用 */
    fullscreen: {type: Boolean, default: false},
  },
  data() {
    return {
      loading: !this.test,
      formLoading: false,
      acting: false,
      syncingCloneExecutors: false,
      form: {},
      formData: {},
      nodeData: {},
      acl: {},
      runtime: null,
      actors: [],
      actorId: null,
      historyNodes: [],
      historyNewestFirst: true,
      sideInfoTab: 'history',
      statusText: '配置中',
      pendingTasks: [],
      visibleTasks: [],
      selectedTaskId: null,
      availableActions: [],
      access: {canView: true, canAct: true, canCancel: false, pendingCount: 0},
      comment: '',
      opinionAgree: 'agree',
      actionAttachments: [],
      errorMsg: '',
      graphData: null,
      graphStatusMap: {},
      graphRev: 0,
      phase: 'config', // config | running
      currentViewNodeId: null,
      currentFormId: null,
      cacheRestoring: false,
      timeoutTimer: null,
    };
  },
  computed: {
    ...mapGetters('session', ['session', 'users', 'deps']),
    isConfig() {
      return this.test && this.phase === 'config';
    },
    /** 测试运行中，或线上实例已加载 */
    isRunning() {
      return this.phase === 'running';
    },
    isLive() {
      return !this.test;
    },
    displayTitle() {
      return this.flow.name ||
        (this.flow.version && this.flow.version.name) ||
        (this.test ? '流程测试' : '流程实例');
    },
    phaseSub() {
      if (!this.test) {
        return this.statusText || this.flow.status || this.flow.description || '';
      }
      return this.isConfig ?
        '先填写表单并配置节点负责人，再发起本地模拟' :
        '本地全数据模拟（含 clone 并行线程）';
    },
    formEditable() {
      if (this.isConfig) return true;
      if (!this.isRunning) return true;
      if (!this.access.canView) return false;
      // 无本人待办（含刚提交完）：只读可见
      if (!this.activePendingTask || this.activePendingTask.peerView) return false;
      return !!this.access.canAct;
    },
    canFillPrimaryTodo() {
      if (!this.access.canAct) return false;
      if (!this.primaryTodo) return false;
      return !this.primaryTodo.peerView;
    },
    showForm() {
      if (this.isConfig) return true;
      if (!this.test || !this.runtime || !this.runtime.instance) return true;
      if (!this.access.canView) return false;
      return true;
    },
    /**
     * 左侧表单上下文任务：本人待办优先；否则用最近已处理任务（克隆提交后仍按该节点 ACL 只读展示）
     */
    viewContextTask() {
      const pending = this.activePendingTask;
      if (pending && !pending.peerView) return pending;
      if (!this.runtime || typeof this.runtime.getLastParticipatedTaskForActor !== 'function') {
        return pending || null;
      }
      const last = this.runtime.getLastParticipatedTaskForActor(this.actorId);
      if (last) return last;
      return pending || null;
    },
    /** 当前应套用视图/ACL 的节点 */
    currentActionNodeId() {
      const task = this.viewContextTask;
      if (task && task.nodeId) return task.nodeId;
      const act = (this.availableActions || []).find((a) => a.key !== 'cancel' && a.nodeId);
      if (act && act.nodeId) return act.nodeId;
      // 可撤回时仍按撤回对应节点展示
      if (this.withdrawAction && this.withdrawAction.nodeId) {
        return this.withdrawAction.nodeId;
      }
      return null;
    },
    activePendingTask() {
      const cards = this.displayTodoCards || [];
      if (!cards.length) return null;
      if (this.selectedTaskId) {
        const selected = cards.find((t) => t.id === this.selectedTaskId);
        if (selected) return selected;
      }
      // 优先本人可处理的待办（非 peerView）
      const mine = cards.find((t) => !t.peerView);
      return mine || cards[0];
    },
    /** 右侧待办卡片：平行可见时含兄弟线程（peerView） */
    displayTodoCards() {
      if (this.visibleTasks && this.visibleTasks.length) {
        return this.visibleTasks;
      }
      if (this.pendingTasks && this.pendingTasks.length) {
        return this.pendingTasks;
      }
      const weakActs = (this.availableActions || []).filter((a) => a.weakSkip && a.nodeId);
      if (!weakActs.length || !this.runtime) return [];
      const seen = {};
      const cards = [];
      weakActs.forEach((a) => {
        if (seen[a.nodeId]) return;
        seen[a.nodeId] = true;
        const node = this.runtime.getNode(a.nodeId);
        const opt = (node && (node.properties || node.option)) || {};
        cards.push({
          id: a.taskId || ('virtual-' + a.nodeId),
          nodeId: a.nodeId,
          type: (node && (node.type || (node.graph && node.graph.type))) || 'approval_node',
          name: opt.name || (node && node.text && node.text.value) || a.label || '下一节点',
          assignees: this.actorId ? [this.actorId] : [],
          weakSkip: true,
          weak: false,
          parallel: false,
          peerView: false,
        });
      });
      return cards;
    },
    primaryTodo() {
      return this.activePendingTask;
    },
    /** MultiForm 平行线程上下文：待办或最近已处理的克隆任务 */
    cloneFormContext() {
      if (!this.isRunning || !this.runtime || !this.viewContextTask) return null;
      if (typeof this.runtime.getCloneFormContext !== 'function') return null;
      return this.runtime.getCloneFormContext(this.viewContextTask);
    },
    /** ACL 变化时强制重挂表单，避免隐藏字段残留 */
    aclRenderKey() {
      const a = this.acl || {};
      const keys = Object.keys(a).sort();
      if (!keys.length) return '0';
      return keys.map((k) => `${k}:${a[k]}`).join('|').slice(0, 120);
    },
    /** 当前待办节点审批意见模式（必须/可选 → 右侧意见面板） */
    showOpinionPanel() {
      const todo = this.primaryTodo;
      if (!todo || !this.runtime) return false;
      const node = this.runtime.getNode(todo.nodeId);
      if (!node) return false;
      const nt = node.type || (node.graph && node.graph.type) || '';
      if (nt !== 'approval_node') return false;
      const opt = node.properties || node.option || {};
      return isOpinionEnabled(opt);
    },
    /** 「表单」模式：右侧提交（非弱审批时含同意/退回） */
    showFormOpinionSubmit() {
      const todo = this.primaryTodo;
      if (!todo || !this.runtime) return false;
      const node = this.runtime.getNode(todo.nodeId);
      if (!node) return false;
      const nt = node.type || (node.graph && node.graph.type) || '';
      if (nt !== 'approval_node') return false;
      const opt = node.properties || node.option || {};
      return isOpinionFormMode(opt);
    },
    /** 当前待办为弱审批：只提意见，提交后默认前进，无同意/退回 */
    isPrimaryTodoWeak() {
      const todo = this.primaryTodo;
      if (!todo || !this.runtime) return false;
      // weakSkip 是后继节点跳过弱审批，不是本节点弱审批
      if (todo.weakSkip) return false;
      if (todo.weak) return true;
      const node = this.runtime.getNode(todo.nodeId);
      if (!node) return false;
      const nt = node.type || (node.graph && node.graph.type) || '';
      if (nt !== 'approval_node') return false;
      const opt = node.properties || node.option || {};
      return !!opt.weak;
    },
    opinionRequired() {
      const todo = this.primaryTodo;
      if (!todo || !this.runtime) return false;
      const node = this.runtime.getNode(todo.nodeId);
      if (!node) return false;
      const opt = node.properties || node.option || {};
      return isOpinionRequired(opt);
    },
    displayHistoryNodes() {
      const list = Array.isArray(this.historyNodes) ? this.historyNodes.slice() : [];
      const newestFirst = this.historyNewestFirst !== false;
      list.sort((a, b) => {
        const ta = a && a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b && b.created_at ? new Date(b.created_at).getTime() : 0;
        if (ta !== tb) {
          return newestFirst ? (tb - ta) : (ta - tb);
        }
        // 同秒时按 id 兜底，保证稳定
        const ia = String((a && a.id) || '');
        const ib = String((b && b.id) || '');
        return newestFirst ? ib.localeCompare(ia) : ia.localeCompare(ib);
      });
      return list;
    },
    /** 过程附件：历史操作附件 + 表单内文件/图片 */
    processFiles() {
      const seen = new Set();
      const out = [];
      const push = (raw, source) => {
        if (!raw || typeof raw !== 'object') return;
        const name = raw.name || raw.filename || raw.title || '';
        const url = raw.url || raw.path || '';
        const fileId = raw.file_id || raw.id || '';
        if (!name && !url && !fileId) return;
        // 排除明显不是文件的业务对象
        if (!url && !fileId && !raw.ext && !/\.\w{1,8}$/.test(String(name))) return;
        const key = String(fileId || `${name}|${url}`);
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          ...raw,
          name: name || '附件',
          url,
          file_id: fileId || null,
          source: source || '',
          uid: key,
        });
      };
      (this.historyNodes || []).forEach((h) => {
        const source = h.nodeName || h.action || '历史';
        (h.attachments || []).forEach((f) => push(f, source));
      });
      const walk = (val, pathHint) => {
        if (val == null) return;
        if (Array.isArray(val)) {
          const looksFileList = val.some((item) =>
            item && typeof item === 'object' && (item.url || item.file_id || item.ext ||
              (item.name && /\.\w{1,8}$/.test(String(item.name)))));
          if (looksFileList) {
            val.forEach((item) => push(item, pathHint || '表单'));
            return;
          }
          val.forEach((item) => walk(item, pathHint));
          return;
        }
        if (typeof val === 'object') {
          if (val.url || val.file_id || val.ext) {
            push(val, pathHint || '表单');
            return;
          }
          Object.keys(val).forEach((k) => {
            const nextHint = pathHint ? `${pathHint}.${k}` : k;
            // 常见附件字段名直接采
            if (k === 'files' || k === 'attachments' || k === 'images' || k === 'photos') {
              walk(val[k], pathHint || k);
            } else {
              walk(val[k], nextHint);
            }
          });
        }
      };
      walk(this.formData, '');
      return out;
    },
    primaryActions() {
      return (this.availableActions || []).filter((a) => a.primary && a.key !== 'cancel');
    },
    secondaryActions() {
      return (this.availableActions || []).filter((a) => !a.primary || a.key === 'cancel');
    },
    cancelActions() {
      return (this.availableActions || []).filter((a) => a.key === 'cancel');
    },
    /** 全局最后一次提交且可撤回时展示 */
    withdrawAction() {
      return this.cancelActions[0] || null;
    },
    withdrawHint() {
      const act = this.withdrawAction;
      if (!act || !this.runtime) return '';
      const node = act.nodeId ? this.runtime.getNode(act.nodeId) : null;
      const name = node ?
        ((node.properties && node.properties.name) ||
          (node.text && node.text.value) ||
          act.nodeId) :
        '';
      return name ? `您刚提交了「${name}」，下一节点尚未处理，可撤回` : '您为最后提交人，下一节点尚未处理，可撤回';
    },
    /** 配置用扁平节点列表（含 option） */
    flatConfigNodes() {
      return this.nodeLevels.flat();
    },
    testCacheKey() {
      if (!this.test) return '';
      const flowId = this.flowId || this.flow?.id || 'draft';
      return `flowTest:${flowId}:${this.graphSignature(this.graph || this.version)}`;
    },
    /** 按 BFS 分层的节点（create 形态，供 NodeCreate 使用） */
    nodeLevels() {
      if (!this.runtime) return [];
      const nodes = (this.runtime.nodes || []).map((n) => ({
        id: n.id,
        option: n.properties || {},
        graph: {type: n.type, text: n.text},
      }));
      const edges = (this.runtime.edges || []).map((e) => ({
        from: e.sourceNodeId || e.from,
        to: e.targetNodeId || e.to,
      }));
      return buildConfigNodeLevels(nodes, edges);
    },
  },
  watch: {
    flowId: {
      handler(val) {
        if (!this.test && val) this.loadingFlow(val);
      },
      immediate: true,
    },
    /** 线上：页面把 assembleGet 整包塞进 flow */
    flow: {
      handler(val) {
        if (this.test) return;
        if (val && val.id && (val.graph || val.tasks || val.formData != null || val.currentView)) {
          this.applyRemoteInstance(val);
        } else if (!val || !val.id) {
          this.loading = true;
          this.phase = 'config';
        }
      },
      deep: true,
      immediate: true,
    },
    graph: {
      handler() {
        if (this.test) this.bootstrapTest();
      },
      deep: true,
      immediate: true,
    },
    version: {
      handler() {
        if (this.test && !this.graph) this.bootstrapTest();
      },
      deep: true,
    },
    comment() {
      this.persistTestCache();
    },
    actionAttachments: {
      handler() {
        this.persistTestCache();
      },
      deep: true,
    },
    isRunning(v) {
      if (v) this.startTimeoutTicker();
      else this.stopTimeoutTicker();
    },
    showOpinionPanel(v) {
      if (v) {
        this.opinionAgree = 'agree';
      }
    },
    showFormOpinionSubmit(v) {
      if (v) {
        this.opinionAgree = 'agree';
      }
    },
    primaryTodo: {
      handler() {
        if (this.showOpinionPanel || this.showFormOpinionSubmit) {
          this.opinionAgree = 'agree';
        }
      },
    },
  },
  beforeDestroy() {
    this.stopTimeoutTicker();
  },
  methods: {
    moment,
    formatTime(t) {
      return t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '';
    },
    /** 历史：默认倒序，可切正序 */
    toggleHistoryOrder() {
      this.historyNewestFirst = !this.historyNewestFirst;
    },
    filePreviewUrl(f) {
      const url = f && f.url ? String(f.url) : '';
      if (!url && f && f.file_id) {
        const cosBase = (this.$cos && this.$cos.defaults && this.$cos.defaults.baseURL) ||
          'https://nbgzfiles-1257839135.cos.ap-shanghai.myqcloud.com/';
        return `${String(cosBase).replace(/\/$/, '')}/${f.file_id}`;
      }
      if (!url) return '';
      if (/^(https?:|blob:)/i.test(url)) return url;
      const cosBase = (this.$cos && this.$cos.defaults && this.$cos.defaults.baseURL) ||
        'https://nbgzfiles-1257839135.cos.ap-shanghai.myqcloud.com/';
      const base = String(cosBase).replace(/\/$/, '');
      const path = url.startsWith('/') ? url.slice(1) : url;
      return `${base}/${path}`;
    },
    isProcessImage(f) {
      if (!f) return false;
      const ext = String(f.ext || '').toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return true;
      const name = String(f.name || '').toLowerCase();
      if (/\.(jpg|jpeg|png|gif|webp|bmp)$/.test(name)) return true;
      const type = String(f.type || f.mime || '').toLowerCase();
      return type.indexOf('image') === 0;
    },
    processFileIcon(f) {
      if (this.isProcessImage(f)) return 'ios-image';
      const name = String((f && f.name) || '').toLowerCase();
      if (name.endsWith('.pdf')) return 'ios-document';
      return 'md-attach';
    },
    typeLabel(t) {
      return {
        start_node: '发起',
        approval_node: '审批',
        carbon_node: '抄送',
        finish_node: '结束',
      }[t] || t;
    },
    typeIcon(t) {
      return {
        start_node: 'md-play',
        approval_node: 'md-checkmark-circle',
        carbon_node: 'md-mail-open',
        finish_node: 'md-flag',
      }[t] || 'md-radio-button-on';
    },
    startTimeoutTicker() {
      this.stopTimeoutTicker();
      if (!this.test) return;
      this.timeoutTimer = setInterval(() => {
        this.pollTimeouts();
      }, 3000);
      this.pollTimeouts();
    },
    stopTimeoutTicker() {
      if (this.timeoutTimer) {
        clearInterval(this.timeoutTimer);
        this.timeoutTimer = null;
      }
    },
    pollTimeouts() {
      if (!this.runtime || !this.isRunning) return;
      const state = this.runtime.tickTimeouts();
      if (state) {
        this.applyState(state);
        this.syncCurrentNodeView();
      }
    },
    needsNodeConfig(node) {
      return node?.graph?.type !== 'finish_node';
    },
    nodeLabel(node) {
      return node?.option?.name || node?.graph?.text?.value || node?.id;
    },
    assigneeNames(task) {
      const ids = task.assignees || [];
      if (!ids.length) return '未指定';
      return ids.map((id) => {
        const u = (this.users || []).find((x) => String(x.id) === String(id));
        return u?.name || id;
      }).join('、');
    },
    selectTodoTask(taskId) {
      this.selectedTaskId = taskId || null;
      this.$nextTick(() => this.syncCurrentNodeView());
    },
    cloneItemKey(nodeId, raw, clonedBy, clonedType) {
      const meta = resolveCloneItemMeta(raw, clonedBy, {
        deps: this.deps || [],
        users: this.users || [],
        clonedType,
      });
      return `${nodeId}_${meta.keyPart}`;
    },
    cloneItemLabel(raw, clonedBy, clonedType) {
      return resolveCloneItemMeta(raw, clonedBy, {
        deps: this.deps || [],
        users: this.users || [],
        clonedType,
      }).namePart;
    },
    getClonedByValues(node) {
      const clonedBy = node.option?.clonedBy;
      if (!clonedBy) return null;
      const path = typeof clonedBy === 'string' ? clonedBy : (clonedBy.key || '');
      if (!path) return null;
      const paths = path.split('.');
      let value = this.formData;
      for (let i = 0; i < paths.length; i++) {
        if (value == null) return null;
        value = value[paths[i]];
      }
      return value;
    },
    getClonedByLabel(node) {
      const clonedBy = node.option?.clonedBy;
      if (!clonedBy) return node.option?.cloneBy || '';
      if (typeof clonedBy === 'string') return clonedBy;
      return clonedBy.control?.name || clonedBy.key || node.option?.cloneBy || '';
    },
    hasExecutors(nodeVal = {}) {
      if (Array.isArray(nodeVal.executors)) return nodeVal.executors.length > 0;
      return !!nodeVal.executors;
    },
    /** 表单变更：不 deep 触发克隆重建；仅配置阶段在事业部等签名变化时由 NodeCreate 自行同步 */
    handleFormInput(v) {
      const next = v && typeof v === 'object' ? {...v} : {};
      this.formData = next;
      if (this.runtime && this.runtime.instance) {
        this.runtime.instance.formData = next;
      }
      if (this.test && this.isConfig) {
        this.persistTestCache();
      }
    },
    /** 节点配置变更；同克隆对象自动同步负责人（独立负责人除外） */
    onNodeConfigInput(nodeId, val) {
      if (this.syncingCloneExecutors) {
        this.$set(this.nodeData, nodeId, val);
        this.persistTestCache();
        return;
      }
      this.syncingCloneExecutors = true;
      try {
        const next = syncCloneExecutorsAcrossNodes(
          this.nodeData,
          this.flatConfigNodes,
          nodeId,
          val,
        );
        this.nodeData = syncAllSharedCloneGroups(next, this.flatConfigNodes);
        this.persistTestCache();
      } finally {
        this.$nextTick(() => {
          this.syncingCloneExecutors = false;
        });
      }
    },
    queueSharedCloneSync() {
      this.$nextTick(() => {
        if (this.syncingCloneExecutors) return;
        const next = syncAllSharedCloneGroups(this.nodeData, this.flatConfigNodes);
        if (next !== this.nodeData) {
          this.nodeData = next;
          this.persistTestCache();
        }
      });
    },
    isCloneFollower(node) {
      return isCloneShareFollower(this.flatConfigNodes, node);
    },
    applyState(state) {
      if (!state) return;
      this.actors = state.actors || [];
      this.actorId = state.actorId;
      this.access = state.access || {canView: true, canAct: true, canCancel: false, pendingCount: 0};
      this.historyNodes = (state.instance && state.instance.history) || [];
      this.statusText = (state.instance && state.instance.status) || (state.prepared ? '配置中' : '');
      // 优先用运行时按身份过滤后的待办 / 可见平行任务
      if (Array.isArray(state.visibleTasks)) {
        this.visibleTasks = state.visibleTasks;
      } else {
        this.visibleTasks = [];
      }
      if (Array.isArray(state.pendingTasks)) {
        this.pendingTasks = state.pendingTasks;
      } else {
        this.pendingTasks = ((state.instance && state.instance.tasks) || [])
          .filter((t) => t.status === 'pending');
      }
      // 选中任务若已不在可见列表中则回退
      const cards = this.visibleTasks.length ? this.visibleTasks : this.pendingTasks;
      if (this.selectedTaskId && !cards.some((t) => t.id === this.selectedTaskId)) {
        this.selectedTaskId = null;
      }
      this.availableActions = state.availableActions || [];
      // 流程图：每次状态变更强制取最新执行态并 remount（LogicFlow 同 id 不一定重画自定义节点）
      this.refreshGraphView();
      if (state.instance && state.instance.formData) {
        this.formData = {...state.instance.formData};
      }
      if (state.instance && state.instance.nodeData) {
        this.nodeData = {...state.instance.nodeData};
      }
      this.phase = state.instance ? 'running' : 'config';
      if (this.phase === 'running') {
        this.$nextTick(() => this.syncCurrentNodeView());
      }
      this.persistTestCache();
    },
    /** 刷新右上角示意图执行态 */
    refreshGraphView() {
      if (!this.runtime) return;
      try {
        this.graphData = this.runtime.getGraphData();
        this.graphStatusMap = typeof this.runtime.getNodeStatusMap === 'function' ?
          (this.runtime.getNodeStatusMap() || {}) :
          {};
        this.graphRev += 1;
      } catch (e) {
        if (!this.graphData) {
          this.graphData = {nodes: [], edges: []};
        }
      }
    },
    /** 按当前待办 / 可执行动作节点切换视图 + ACL（弱审批跳过时用下一节点） */
    syncCurrentNodeView() {
      if (!this.isRunning) return;
      const nodeId = this.currentActionNodeId || this.currentViewNodeId;
      let formId = null;
      let nextAcl = {};
      if (this.runtime) {
        formId = this.runtime.getNodeViewId(nodeId);
        nextAcl = this.runtime.getNodeAcl(nodeId) || {};
      } else if (this.flow && this.flow.currentView) {
        formId = this.flow.currentView.formId;
        nextAcl = this.flow.currentView.acl || {};
      }
      this.currentViewNodeId = nodeId;
      this.acl = {...nextAcl};
      if (!formId) {
        // 保留已有表单作只读回退
        if (this.form && this.form.fields) {
          this.applyFormAclPrep(this.form, this.acl);
        }
        return;
      }
      if (String(this.currentFormId) === String(formId) && this.form && this.form.id) {
        // 同表单也要重写 fullkey / 内嵌 ACL，否则权限配置不生效
        this.applyFormAclPrep(this.form, this.acl);
        return;
      }
      this.loadFormById(formId);
    },
    /**
     * 线上实例：消费 assembleGet 返回包
     * { formData, nodeData, tasks, history, access, availableActions, currentView, graph, status... }
     */
    applyRemoteInstance(payload) {
      if (!payload || !payload.id) return;
      const graph = payload.graph || {};
      const actorId = this.session && this.session.id;
      try {
        this.runtime = new FlowLocalRuntime({
          flowDef: {
            id: payload.flow_id,
            name: (payload.version && payload.version.name) || payload.name || '流程实例',
          },
          graph: {nodes: graph.nodes || [], edges: graph.edges || []},
          actorId,
          userPool: this.users || [],
          deps: this.deps || [],
        });
        // 仅挂展示用 instance，动作走后端 Patch
        this.runtime.instance = {
          id: payload.id,
          status: payload.status,
          created_by: payload.created_by,
          created_at: payload.created_at,
          formData: payload.formData || {},
          nodeData: payload.nodeData || {},
          tasks: payload.tasks || [],
          history: payload.history || [],
          completedNodeIds: payload.completedNodeIds || [],
          ccRecipients: payload.ccRecipients || [],
          canCancel: !!payload.canCancel,
        };
      } catch (e) {
        this.runtime = null;
        this.errorMsg = e.message || String(e);
      }

      this.actorId = actorId != null ? String(actorId) : null;
      this.access = payload.access || {
        canView: true, canAct: false, canCancel: false, pendingCount: 0,
      };
      this.historyNodes = payload.history || [];
      this.statusText = payload.status || '';
      this.pendingTasks = Array.isArray(payload.pendingTasks) ? payload.pendingTasks : [];
      this.visibleTasks = Array.isArray(payload.visibleTasks) ? payload.visibleTasks : [];
      this.availableActions = payload.availableActions || [];
      this.formData = {...(payload.formData || {})};
      this.nodeData = {...(payload.nodeData || {})};
      this.phase = 'running';

      this.graphData = {
        nodes: graph.nodes || [],
        edges: graph.edges || [],
      };
      this.graphStatusMap = graph.statusMap || {};
      this.graphRev += 1;

      const view = payload.currentView || {};
      this.currentViewNodeId = view.nodeId || null;
      this.acl = {...(view.acl || {})};
      if (view.formId) {
        this.loadFormById(view.formId);
      }
      // 闭合/无待办时按上一处理节点 ACL 再校正一次（避免一直用开始节点可见性）
      this.$nextTick(() => this.syncCurrentNodeView());
      this.loading = false;
    },
    reloadRemoteInstance() {
      const id = (this.flow && this.flow.id) || this.flowId;
      if (!id) return Promise.resolve();
      this.acting = true;
      return this.$api.get(`/oa/flow-instances/${id}`).then((res) => {
        const data = res.data.data;
        // 通知父页同步，同时本地立刻刷新
        this.$emit('loaded', data);
        this.applyRemoteInstance(data);
      }).catch((e) => {
        this.errorMsg = e.message || String(e);
        this.Error && this.Error(this.errorMsg);
      }).finally(() => {
        this.acting = false;
      });
    },
    runRemoteAction(act) {
      if (!act) return;
      const id = (this.flow && this.flow.id) || this.flowId;
      if (!id) {
        this.errorMsg = '缺少实例 id';
        this.Error && this.Error(this.errorMsg);
        return;
      }
      this.errorMsg = '';
      this.acting = true;
      let data = null;
      try {
        if (this.formEditable && this.$refs.form &&
          (act.key === 'submit' || act.key === 'approve' || act.weakSkip)) {
          if (act.key === 'submit') {
            data = this.$refs.form.submit();
            if (!data) throw new Error('表单未填写完整');
          } else {
            data = this.formData;
          }
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
        this.Error && this.Error(this.errorMsg);
        this.acting = false;
        return;
      }
      const body = {
        action: act.key,
        taskId: act.taskId,
        nodeId: act.nodeId,
        threadId: act.threadId,
        comment: this.comment,
        attachments: this.actionAttachments,
      };
      if (data) body.data = data;
      this.$api.patch(`/oa/flow-instances/${id}`, body).then((res) => {
        this.comment = '';
        this.opinionAgree = 'agree';
        this.actionAttachments = [];
        const next = res.data && res.data.data;
        if (next && next.id) {
          this.$emit('loaded', next);
          this.applyRemoteInstance(next);
        } else {
          return this.reloadRemoteInstance();
        }
        this.Success && this.Success((act.label || act.key) + '成功');
      }).catch((e) => {
        this.errorMsg = e.message || String(e);
        this.Error && this.Error(this.errorMsg);
      }).finally(() => {
        this.acting = false;
      });
    },
    /** 对齐字段 fullkey，并注入 MultiForm/InnerForm 相对 ACL */
    applyFormAclPrep(form, aclMap) {
      if (!form || !Array.isArray(form.fields)) return;
      prepareFormForAcl(form, aclMap || {});
      // 触发视图刷新（就地改 fields 时 Vue 不一定感知）
      this.form = {...form, fields: [...(form.fields || [])]};
    },
    graphSignature(graph) {
      const g = graph || {};
      const nodes = g.nodes || [];
      const edges = g.edges || g.actions || [];
      const nids = nodes.map((n) => n.id).filter(Boolean).sort().join(',');
      const eids = edges.map((e) => `${e.sourceNodeId || e.source || ''}>${e.targetNodeId || e.target || ''}`).sort().join(',');
      return `${nodes.length}_${edges.length}_${nids}_${eids}`.slice(0, 180);
    },
    readTestCache() {
      if (!this.test || !this.testCacheKey) return null;
      try {
        const raw = localStorage.getItem(this.testCacheKey);
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    },
    persistTestCache() {
      if (!this.test || !this.testCacheKey || this.cacheRestoring) return;
      try {
        const payload = {
          version: 1,
          savedAt: Date.now(),
          phase: this.phase,
          formData: this.formData,
          nodeData: this.nodeData,
          comment: this.comment,
          actionAttachments: this.actionAttachments,
          runtime: this.runtime ? this.runtime.serialize() : null,
        };
        localStorage.setItem(this.testCacheKey, JSON.stringify(payload));
      } catch (e) {
        // quota / private mode
      }
    },
    clearTestCache() {
      if (!this.testCacheKey) return;
      try {
        localStorage.removeItem(this.testCacheKey);
      } catch (e) {
        // ignore
      }
    },
    resetTestCache() {
      this.clearTestCache();
      this.comment = '';
      this.actionAttachments = [];
      this.errorMsg = '';
      this.bootstrapTest({forceFresh: true});
      this.Success && this.Success('已重置测试缓存');
    },
    loadFormById(formId) {
      if (!formId) {
        this.form = {};
        this.currentFormId = null;
        this.formLoading = false;
        return;
      }
      this.formLoading = true;
      this.$api.get(`forms/${formId}`).then((res) => {
        const form = res.data.data || {};
        // 运行阶段按当前节点 ACL 对齐 fullkey；配置阶段不隐藏字段
        if (this.isRunning) {
          prepareFormForAcl(form, this.acl || {});
        } else {
          prepareFormForAcl(form, {});
        }
        this.form = form;
        this.currentFormId = formId;
      }).catch((e) => {
        this.errorMsg = '表单加载失败：' + (e.message || e);
      }).finally(() => {
        this.formLoading = false;
      });
    },
    bootstrapTest(opts = {}) {
      this.loading = true;
      this.formLoading = true;
      this.errorMsg = '';
      this.phase = 'config';
      this.nodeData = {};
      this.formData = {};
      this.form = {};
      this.historyNodes = [];
      this.pendingTasks = [];
      this.visibleTasks = [];
      this.selectedTaskId = null;
      this.availableActions = [];
      this.actionAttachments = [];
      this.comment = '';
      this.$nextTick(() => {
        try {
          const graph = this.graph && (this.graph.nodes || this.graph.edges) ?
            this.graph :
            this.version;
          const actorId = this.session?.id;
          this.runtime = new FlowLocalRuntime({
            flowDef: this.flow || {},
            graph,
            actorId,
            userPool: this.users || [],
            deps: this.deps || [],
          });

          const cache = opts.forceFresh ? null : this.readTestCache();
          if (cache && cache.runtime && cache.runtime.instance && cache.phase === 'running') {
            this.cacheRestoring = true;
            try {
              this.comment = cache.comment || '';
              this.actionAttachments = Array.isArray(cache.actionAttachments) ?
                cache.actionAttachments : [];
              if (cache.formData) this.formData = {...cache.formData};
              if (cache.nodeData) this.nodeData = {...cache.nodeData};
              const state = this.runtime.hydrate(cache.runtime);
              this.applyState(state);
              this.loading = false;
              this.formLoading = false;
              this.Success && this.Success('已恢复上次测试进度');
            } finally {
              this.$nextTick(() => {
                this.cacheRestoring = false;
                this.persistTestCache();
              });
            }
            return;
          }

          // 配置草稿恢复
          if (cache && cache.phase === 'config') {
            if (cache.formData) this.formData = {...cache.formData};
            if (cache.nodeData) this.nodeData = {...cache.nodeData};
          }

          const state = this.runtime.prepare({actorId});
          this.applyState(state);
          this.statusText = '配置中';
          this.loading = false;
          this.loadStartForm();
        } catch (e) {
          this.errorMsg = e.message || String(e);
          this.Error && this.Error(this.errorMsg);
          this.formLoading = false;
          this.loading = false;
        }
      });
    },
    loadStartForm() {
      const formId = this.runtime && this.runtime.getStartFormId();
      // 配置/发起阶段需要完整填写表单，不应用节点「隐藏」ACL（否则左侧会整表空白）
      this.acl = {};
      this.currentViewNodeId = null;
      this.loadFormById(formId);
    },
    validateForm() {
      if (!this.$refs.form) {
        this.errorMsg = '表单未加载完成，请稍后重试';
        return null;
      }
      const formData = this.$refs.form.submit();
      if (!formData) {
        this.errorMsg = '表单未填写完整，请检查必填项';
        return null;
      }
      return formData;
    },
    validateNodeConfig() {
      const flat = this.nodeLevels.flat().filter((n) => this.needsNodeConfig(n));
      if (!flat.length) {
        this.errorMsg = '流程配置未加载，请稍后重试';
        return false;
      }
      for (const node of flat) {
        const label = this.nodeLabel(node);
        const nodeVal = this.nodeData[node.id];
        if (node.option?.clonedBy) {
          const relatedValues = this.getClonedByValues(node);
          const fieldLabel = this.getClonedByLabel(node);
          if (!Array.isArray(relatedValues) || relatedValues.length === 0) {
            this.errorMsg = `流程配置不完整：节点「${label}」需先填写关联字段「${fieldLabel}」`;
            return false;
          }
          const cloned = nodeVal?.cloned || {};
          for (const item of relatedValues) {
            const key = this.cloneItemKey(
              node.id,
              item,
              node.option.clonedBy,
              node.option.clonedType,
            );
            const itemLabel = this.cloneItemLabel(
              item,
              node.option.clonedBy,
              node.option.clonedType,
            );
            if (!this.hasExecutors(cloned[key])) {
              const who = node.graph?.type === 'carbon_node' ? '抄送人' : '负责人';
              this.errorMsg = `流程配置不完整：节点「${label}」的「${itemLabel}」未选择${who}`;
              return false;
            }
          }
          continue;
        }
        // 开始节点：未配负责人时默认当前用户
        if (node.graph?.type === 'start_node') {
          if (!this.hasExecutors(nodeVal) && !this.actorId) {
            this.errorMsg = `流程配置不完整：节点「${label}」未选择负责人`;
            return false;
          }
          continue;
        }
        if (!this.hasExecutors(nodeVal)) {
          const who = node.graph?.type === 'carbon_node' ? '抄送人' : '负责人';
          this.errorMsg = `流程配置不完整：节点「${label}」未选择${who}`;
          return false;
        }
      }
      return true;
    },
    startTest() {
      if (!this.runtime) return;
      this.errorMsg = '';
      this.acting = true;
      try {
        this.nodeData = syncAllSharedCloneGroups(this.nodeData, this.flatConfigNodes);
        const formData = this.validateForm();
        if (!formData) {
          this.Error && this.Error(this.errorMsg);
          return;
        }
        if (!this.validateNodeConfig()) {
          this.Error && this.Error(this.errorMsg);
          return;
        }
        const state = this.runtime.start({
          formData,
          nodeData: this.nodeData,
          actorId: this.actorId || this.session?.id,
        });
        this.applyState(state);
        // 发起后若当前身份已无待办，自动切到第一个可处理身份
        if (state.access && !state.access.canAct) {
          const next = (state.actors || []).find((a) => a.canAct);
          if (next) {
            this.applyState(this.runtime.setActor(next.id));
          }
        }
        this.syncCurrentNodeView();
        this.Success && this.Success('已发起测试');
      } catch (e) {
        this.errorMsg = e.message || String(e);
        this.Error && this.Error(this.errorMsg);
      } finally {
        this.acting = false;
      }
    },
    switchActor(id) {
      if (!this.runtime) return;
      this.errorMsg = '';
      const state = this.runtime.setActor(id);
      this.applyState(state);
      this.syncCurrentNodeView();
    },
    runAction(act) {
      if (!act) return;
      if (!this.test) {
        this.runRemoteAction(act);
        return;
      }
      if (!this.runtime) return;
      this.errorMsg = '';
      this.acting = true;
      try {
        if (act.key === 'cancel') {
          const state = this.runtime.cancel({
            actorId: this.actorId,
            cancelIndex: act.cancelIndex,
          });
          this.applyState(state);
          this.syncCurrentNodeView();
          this.Success && this.Success('已撤回');
          return;
        }

        let data = null;
        // 可编辑时提交/同意/弱跳过均带上表单增量（按节点 ACL）
        if (this.formEditable && this.$refs.form &&
          (act.key === 'submit' || act.key === 'approve' || act.weakSkip)) {
          if (act.key === 'submit') {
            data = this.$refs.form.submit();
            if (!data) {
              throw new Error('表单未填写完整');
            }
          } else {
            // 审批/跳过：不强制校验必填，只合并当前值
            data = this.formData;
          }
        } else if (act.key === 'submit' && this.isConfig) {
          if (!this.$refs.form) {
            throw new Error('表单未就绪');
          }
          data = this.$refs.form.submit();
          if (!data) {
            throw new Error('表单未填写完整');
          }
        }

        const state = this.runtime.patch({
          action: act.key,
          data,
          actorId: this.actorId,
          taskId: act.taskId,
          nodeId: act.nodeId,
          comment: this.comment,
          attachments: this.actionAttachments,
        });
        this.comment = '';
        this.opinionAgree = 'agree';
        this.actionAttachments = [];
        this.applyState(state);
        this.syncCurrentNodeView();
        this.Success && this.Success(act.label + '成功');
      } catch (e) {
        this.errorMsg = e.message || String(e);
        this.Error && this.Error(this.errorMsg);
      } finally {
        this.acting = false;
      }
    },
    /** 意见模式下：根据是否同意走 approve / reject；弱审批固定前进 */
    submitWithOpinion() {
      if (this.opinionRequired && (!this.comment || !String(this.comment).trim())) {
        this.errorMsg = '请填写审批意见';
        this.Error && this.Error(this.errorMsg);
        return;
      }
      // 弱审批只提意见，提交后默认跳到下一节点
      if (this.isPrimaryTodoWeak) {
        this.opinionAgree = 'agree';
      }
      // 默认同意；若被清空则补回
      if (!this.opinionAgree) {
        this.opinionAgree = 'agree';
      }
      const wantReject = !this.isPrimaryTodoWeak && this.opinionAgree === 'reject';
      const acts = this.availableActions || [];
      let act = null;
      if (wantReject) {
        act = acts.find((a) => a.key === 'reject') ||
          acts.find((a) => a.key === 'approve' || a.key === 'submit');
      } else {
        act = acts.find((a) => a.key === 'approve' || a.key === 'submit') ||
          acts.find((a) => a.primary && a.key !== 'cancel');
      }
      if (!act) {
        this.errorMsg = '当前无可执行操作';
        this.Error && this.Error(this.errorMsg);
        return;
      }
      // 不同意但无 reject 边时，意见里标注
      if (wantReject && act.key !== 'reject') {
        this.comment = `[不同意] ${this.comment || ''}`.trim();
      }
      this.runAction(act);
    },
    // —— 非测试模式（保留原逻辑骨架）——
    getCurrentView() {
      const ver = this.version || {};
      const nodes = ver.nodes || [];
      if (!Array.isArray(nodes)) return;
      const node = nodes.find((n) => (n.type || n.graph?.type) === 'start_node');
      const viewId = node?.properties?.view_id || node?.option?.view_id;
      if (viewId) {
        this.getForm(viewId);
        this.acl = unpackAclMap(node?.properties?.acl || node?.option?.acl);
      }
    },
    getForm(id) {
      this.$api.get(`forms/${id}`).then((res) => {
        this.form = res.data.data;
      });
    },
    loadingFlow() {
      this.loading = true;
      setTimeout(() => {
        this.getCurrentView();
        this.loading = false;
      }, 300);
    },
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="less">
.flow-test-instance {
  /* 嵌入 AppClient / 对话框：占满父级，内部滚动；勿依赖页面级滚动（.container 为 overflow:hidden） */
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;

  .flow-test-skeleton-boot {
    padding: 10px;
  }
  .flow-test-soft-tip {
    padding: 16px 10px;
    text-align: center;
    color: var(--subtext2);
    font-size: 13px;
  }
  &.flow-test-instance--fullscreen {
    height: 100vh;
    padding: 8px 10px 0;
  }
  .flow-test-titlebar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    margin-bottom: 6px;
    padding: 0 2px;
  }
  .flow-test-title-icon {
    flex-shrink: 0;
    color: var(--primary, #2d8cf0);
  }
  .flow-test-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text2, #333);
    max-width: 40%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .flow-test-sub {
    font-size: 12px;
    color: var(--subtext2);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .flow-test-body {
    margin-top: 0;
    flex: 1;
    min-height: 0;
    align-items: stretch;
    gap: 8px;
    overflow: hidden;
  }
  .flow-test-main {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
  .flow-test-content {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    margin: 0;
    padding: 8px;
    background: var(--border);
    border: 1px solid var(--border);
    border-radius: 6px;
    .ugo-form-root {
      margin: 0 auto;
      width: 100% !important;
      max-width: 1000px !important;
    }
  }
  &.flow-test-instance--fullscreen .side-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
  &.flow-test-instance--fullscreen .node-config-panel {
    flex: 1;
    max-height: none;
  }
  &.flow-test-instance--fullscreen .history-timeline {
    flex: 1;
    height: auto;
    max-height: none;
  }
  .side-graph-wrap {
    flex-shrink: 0;
    width: 100%;
    height: 140px;
    margin-bottom: 6px;
    border: 1px solid var(--border);
    border-radius: 6px;
    overflow: hidden;
    background: var(--bg2, #f7f8fa);
    position: relative;
  }
  .side-graph {
    width: 100% !important;
    height: 140px !important;
    position: relative;
  }
  .side-graph-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: var(--subtext2);
  }
  .side-section-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text2, #333);
    margin: 2px 0 6px;
  }
  .flow-test-toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px -10px 0;
    padding: 6px 10px;
    border-top: 1px solid var(--border);
    background: var(--bg2, #f7f8fa);
    min-height: 44px;
    box-sizing: border-box;
  }
  .toolbar-reset {
    flex-shrink: 0;
  }
  .toolbar-status {
    flex-shrink: 0;
    height: 26px;
    min-width: 64px;
    padding: 0 10px;
    border-radius: 6px;
    background: var(--warning);
    color: var(--hover-text, #fff);
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &.config {
      background: var(--info, #2d8cf0);
    }
  }
  .toolbar-divider {
    width: 1px;
    height: 20px;
    background: var(--border);
    flex-shrink: 0;
  }
  .toolbar-tip {
    font-size: 12px;
    color: var(--subtext2);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .actor-bar-label {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--subtext2);
    line-height: 26px;
  }
  .actor-list {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    align-items: center;
  }
  .actor-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 14px;
    border: 1px solid var(--border);
    cursor: pointer;
    font-size: 12px;
    background: #fff;
    flex-shrink: 0;
    transition: border-color .15s ease, background .15s ease, box-shadow .15s ease, color .15s ease;
    &:hover { border-color: var(--hover); }
    &.active {
      border-color: var(--hover, #2d8cf0);
      border-width: 2px;
      color: #fff;
      background: var(--hover, #2d8cf0);
      box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.28);
      font-weight: 600;
      padding: 2px 7px;
      .actor-role {
        color: rgba(255, 255, 255, 0.92);
        background: rgba(255, 255, 255, 0.22);
      }
      .actor-todo-dot {
        background: #fff;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4);
      }
    }
    &.has-todo {
      border-color: #19be6b;
    }
    &.active.has-todo {
      border-color: #19be6b;
      background: #19be6b;
      box-shadow: 0 0 0 2px rgba(25, 190, 107, 0.28);
    }
    &.no-access {
      opacity: 0.55;
    }
  }
  .actor-todo-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #19be6b;
    flex-shrink: 0;
  }
  .actor-name { max-width: 72px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .actor-role {
    font-size: 11px;
    color: var(--subtext2);
    padding: 0 4px;
    border-radius: 4px;
    background: #00000008;
  }
  .actor-empty { font-size: 12px; color: var(--subtext2); line-height: 26px; }

  .todo-card {
    flex-shrink: 0;
    margin-top: 0;
    padding: 8px 10px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 6px;
    box-shadow: none;
    &.todo-card--timeout {
      border-color: #ffd591;
      background: #fffbe6;
    }
  }
  .todo-head { margin-bottom: 6px; }
  .todo-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text2, #333);
    line-height: 1.35;
    word-break: break-all;
  }
  .todo-attrs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
  }
  .todo-thread-switch {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }
  .todo-thread-chip {
    appearance: none;
    border: 1px solid var(--border);
    background: #fafafa;
    color: var(--text2, #333);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    line-height: 20px;
    cursor: pointer;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &.todo-thread-chip--active {
      border-color: var(--hover, #2d8cf0);
      color: var(--hover, #2d8cf0);
      background: rgba(45, 140, 240, 0.08);
    }
    &.todo-thread-chip--peer {
      color: var(--subtext2);
    }
  }
  .todo-thread-chip-tag {
    margin-left: 4px;
    font-size: 11px;
    opacity: 0.75;
  }
  .todo-attr {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: var(--subtext2);
    font-size: 12px;
    max-width: 100%;
    em {
      font-style: normal;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &.todo-attr--warn { color: #d48806; }
    &.todo-attr--peer { color: #8c8c8c; }
    &.todo-attr--people em { max-width: 160px; }
  }
  .todo-comment { margin-top: 6px; }
  .todo-opinion-label {
    font-size: 12px;
    color: var(--text2, #333);
    font-weight: 500;
    margin-top: 8px;
    margin-bottom: 4px;
  }
  .todo-agree-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    .todo-opinion-label { margin: 0; }
  }
  .todo-attach { margin-top: 6px; }
  .todo-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }
  .todo-action-btn {
    height: 36px !important;
    font-size: 13px !important;
    border-radius: 6px !important;
    font-weight: 500;
    letter-spacing: 0.3px;
    box-shadow: none;
    &.todo-action-btn--primary {
      box-shadow: 0 3px 8px rgba(45, 140, 240, 0.24);
    }
  }
  .todo-withdraw-row {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed var(--border, #e8eaec);
  }
  .todo-action-btn--withdraw {
    color: var(--text2, #515a6e) !important;
    border-color: var(--border, #dcdee2) !important;
    background: #fff !important;
    &:hover {
      color: var(--primary, #2d8cf0) !important;
      border-color: var(--primary, #2d8cf0) !important;
    }
  }
  .todo-withdraw-hint {
    font-size: 12px;
    color: var(--subtext2, #808695);
    line-height: 1.5;
    margin-bottom: 8px;
  }
  .action-row { margin-top: 6px; }
  .error-msg { margin-top: 6px; color: var(--error); font-size: 12px; }
  .side-panel {
    margin-left: 0;
    width: 360px;
    border-left: 1px solid var(--border);
    padding-left: 8px;
    flex-shrink: 0;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }
  .node-config-panel {
    overflow-y: auto;
    max-height: 360px;
    background: var(--bg3);
    border-radius: 5px;
    border: 1px solid var(--border);
    padding: 8px;
  }
  .history-block {
    flex: 1;
    min-height: 0;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
  }
  .history-block-head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
    min-height: 28px;
    padding: 0 12px;
  }
  .history-tabs {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    min-width: 0;
  }
  .history-tab {
    border: none;
    background: transparent;
    color: var(--subtext2, #808695);
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
    padding: 0 8px;
    cursor: pointer;
    border-radius: 4px;
    &:hover {
      color: var(--text2, #333);
      background: rgba(0, 0, 0, 0.03);
    }
    &.history-tab--active {
      color: var(--text2, #333);
    }
  }
  .history-tab-count {
    margin-left: 4px;
    font-weight: 500;
    color: var(--subtext2);
  }
  .history-block-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text2, #333);
    margin-bottom: 0;
    line-height: 24px;
  }
  .history-order-toggle {
    flex-shrink: 0;
    height: 24px !important;
    padding: 0 8px !important;
    margin: 0 !important;
    font-size: 12px !important;
    color: var(--primary, #2d8cf0) !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    &:hover, &:focus {
      color: var(--primary, #2d8cf0) !important;
      background: transparent !important;
      opacity: 0.75;
    }
  }
  .history-timeline {
    flex: 1;
    min-height: 140px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 10px 12px;
    background: #fafbfc;
  }
  .process-files-panel {
    flex: 1;
    min-height: 140px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 8px 12px;
    background: #fafbfc;
  }
  .process-file-row {
    & + .process-file-row {
      border-top: 1px dashed var(--border, #e8eaec);
    }
  }
  .process-file-main {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 2px;
    color: inherit;
    text-decoration: none;
    min-width: 0;
    &:hover .process-file-name {
      color: var(--primary, #2d8cf0);
    }
  }
  .process-file-thumb {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid var(--border);
    flex-shrink: 0;
    background: #fff;
  }
  .process-file-icon {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    border: 1px solid var(--border);
    background: #fff;
    color: var(--subtext2);
    flex-shrink: 0;
  }
  .process-file-meta {
    min-width: 0;
    flex: 1;
  }
  .process-file-name {
    font-size: 12px;
    color: var(--text2, #333);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
  .process-file-sub {
    margin-top: 2px;
    font-size: 11px;
    color: var(--subtext2);
    display: flex;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
  }
  .history-row {
    display: flex;
    align-items: stretch;
    position: relative;
    padding-bottom: 16px;
    &:last-child {
      padding-bottom: 2px;
      .history-line { display: none; }
    }
    &.history-row--skip .history-dot {
      background: #fa8c16;
      border-color: #ffe7ba;
    }
    &.history-row--warn .history-dot {
      background: #faad14;
      border-color: #fff1b8;
    }
  }
  .history-rail {
    position: relative;
    width: 14px;
    flex-shrink: 0;
    margin-right: 8px;
  }
  .history-dot {
    position: absolute;
    top: 10px;
    left: 50%;
    width: 10px;
    height: 10px;
    margin-left: -5px;
    border-radius: 50%;
    background: #2d8cf0;
    border: 2px solid #d6e4ff;
    z-index: 1;
    box-sizing: border-box;
  }
  .history-line {
    position: absolute;
    top: 24px;
    bottom: -16px;
    left: 50%;
    width: 2px;
    margin-left: -1px;
    background: #e8e8e8;
  }
  .history-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .history-person {
    min-height: 28px;
    display: flex;
    align-items: center;
  }
  .sys-actor-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sys-actor-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f0f0f0;
    color: #8c8c8c;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sys-actor-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--subtext2);
  }
  .history-body {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 8px 10px;
    box-shadow: none;
  }
  .history-row--skip .history-body {
    border-color: #ffd591;
    background: #fffbe6;
  }
  .history-row--warn .history-body {
    border-color: #ffe58f;
    background: #fffef5;
  }
  .history-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
  }
  .history-time {
    font-size: 11px;
    color: #19be6b;
  }
  .history-node {
    color: var(--subtext2);
    font-size: 11px;
    margin-bottom: 2px;
  }
  .history-content {
    font-size: 12px;
    color: var(--text2, #333);
    line-height: 1.45;
    word-break: break-word;
  }
  .history-files {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .history-file-link {
    font-size: 12px;
    color: #2d8cf0;
    word-break: break-all;
    display: inline-flex;
    align-items: center;
  }
  .sys-actor { font-size: 12px; color: var(--subtext2); }
  .weak-tag {
    color: #ff9900;
    background: #fff7e6;
    padding: 0 6px;
    border-radius: 4px;
  }
  .sla-tag {
    color: #595959;
    background: #f5f5f5;
    padding: 0 6px;
    border-radius: 4px;
    font-size: 11px;
    &.sla-tag--warn {
      color: #d48806;
      background: #fff7e6;
    }
  }
  .skip-tag {
    font-size: 11px;
    color: #ed4014;
    background: #fff1f0;
    padding: 0 6px;
    border-radius: 4px;
    font-weight: 400;
  }
}
</style>
